import { type JSX, useCallback, useEffect, useState } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Group,
  Loader,
  Modal,
  Paper,
  Select,
  Stack,
  Text,
  TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import {
  type Capacidades,
  type ListaTransbordos,
  type StatusTransbordo,
  type TransbordoNaFila,
  api,
  explicarErro,
} from '../lib/api.js';
import { NomeDoAluno } from './NomeDoAluno.js';
import { OrigemDoContato } from './OrigemDoContato.js';
import { BarraDeFiltros, BlocoDeAjuda, Vazio } from './ui.js';

/**
 * Fila de transbordos.
 *
 * A notificação no grupo some na rolagem; esta fila é o registro que garante
 * que nada se perde. O destaque de atraso é a razão de ela existir aberta: é
 * o único lugar que diz "alguém está esperando há tempo demais".
 *
 * O telefone sai mascarado do servidor, como na lista de atendimentos — é
 * tela de balcão. O número completo está na notificação do grupo (interna) e
 * no detalhe do atendimento, para quem tem a capacidade.
 */

const ROTULO_MOTIVO: Record<string, string> = {
  solicitado: 'pediu atendente',
  baixa_confianca: 'lacuna na base',
  bloqueio_pagamento: 'dado de pagamento',
  humano_assumiu: 'humano assumiu',
  nao_informado: '—',
};

/** Minutos de ATENDIMENTO, não de parede. Ver `minutosUteisEntre`. */
function esperaUtil(minutos: number): string {
  if (minutos < 60) return `${minutos}min úteis`;
  const horas = Math.floor(minutos / 60);
  const resto = minutos % 60;
  return resto === 0 ? `${horas}h úteis` : `${horas}h${String(resto).padStart(2, '0')} úteis`;
}

export function FilaTransbordos({
  capacidades,
  aoAbrirAtendimento,
  aoVerConversa,
  aoMudarPendencias,
}: {
  capacidades: Capacidades;
  aoAbrirAtendimento: (atendimentoId: string) => void;
  /** Abre o fio da conversa. Opcional: ver a nota em `Conversas`. */
  aoVerConversa?: (atendimentoId: string) => void;
  aoMudarPendencias: () => void;
}): JSX.Element {
  const [dados, setDados] = useState<ListaTransbordos | null>(null);
  const [status, setStatus] = useState<StatusTransbordo | ''>('pendente');
  const [motivo, setMotivo] = useState('');
  const [dias, setDias] = useState('30');
  const [erro, setErro] = useState<string | null>(null);
  const [anotando, setAnotando] = useState<string | null>(null);
  const [observacao, setObservacao] = useState('');

  const carregar = useCallback(async () => {
    const params = new URLSearchParams({ limite: '200' });
    if (status) params.set('status', status);
    if (motivo) params.set('motivo', motivo);
    if (dias) params.set('dias', dias);
    try {
      setDados(await api.get<ListaTransbordos>(`/admin/transbordos?${params.toString()}`));
      setErro(null);
    } catch (e) {
      setErro(explicarErro(e, 'falha ao carregar a fila de transbordos'));
    }
  }, [status, motivo, dias]);

  useEffect(() => {
    void carregar();
  }, [carregar]);

  async function marcar(item: TransbordoNaFila, texto: string): Promise<void> {
    try {
      await api.post(`/admin/transbordos/${item.atendimentoId}/atendido`, {
        ...(texto.trim() ? { observacao: texto.trim() } : {}),
      });
      setAnotando(null);
      setObservacao('');
      notifications.show({ color: 'green', message: 'Transbordo marcado como atendido.' });
      await carregar();
      aoMudarPendencias();
    } catch (e) {
      setErro(explicarErro(e, 'falha ao marcar como atendido'));
    }
  }

  async function devolver(item: TransbordoNaFila): Promise<void> {
    try {
      await api.post(`/admin/transbordos/${item.atendimentoId}/reabrir`);
      notifications.show({ color: 'green', message: 'Transbordo devolvido à fila.' });
      await carregar();
      aoMudarPendencias();
    } catch (e) {
      setErro(explicarErro(e, 'falha ao devolver à fila'));
    }
  }

  if (!dados) {
    return erro ? (
      <Alert color="red" variant="light" role="alert">
        {erro}
      </Alert>
    ) : (
      <Group gap="xs">
        <Loader size="sm" />
        <Text c="dimmed">Carregando…</Text>
      </Group>
    );
  }

  const atrasados = dados.itens.filter((i) => i.atrasado).length;
  const emFoco = dados.itens.find((i) => i.atendimentoId === anotando) ?? null;

  return (
    <Stack gap="sm">
      <BarraDeFiltros>
        <Select
          size="xs"
          label="Status"
          w={200}
          allowDeselect={false}
          value={status}
          onChange={(v) => setStatus((v ?? '') as StatusTransbordo | '')}
          data={[
            { value: 'pendente', label: `Pendentes (${dados.pendentes})` },
            { value: 'atendido', label: `Atendidos (${dados.atendidos})` },
            { value: '', label: 'Todos' },
          ]}
        />
        <Select
          size="xs"
          label="Motivo"
          w={200}
          allowDeselect={false}
          value={motivo}
          onChange={(v) => setMotivo(v ?? '')}
          data={[
            { value: '', label: 'Qualquer motivo' },
            { value: 'solicitado', label: 'Pediu atendente' },
            { value: 'baixa_confianca', label: 'Lacuna na base' },
            { value: 'bloqueio_pagamento', label: 'Dado de pagamento' },
            { value: 'humano_assumiu', label: 'Humano assumiu' },
          ]}
        />
        <Select
          size="xs"
          label="Período"
          w={170}
          allowDeselect={false}
          value={dias}
          onChange={(v) => setDias(v ?? '')}
          data={[
            { value: '7', label: 'Últimos 7 dias' },
            { value: '30', label: 'Últimos 30 dias' },
            { value: '90', label: 'Últimos 90 dias' },
            { value: '', label: 'Todo o período' },
          ]}
        />
        <Button size="xs" onClick={() => void carregar()}>
          Recarregar
        </Button>
      </BarraDeFiltros>

      {/*
        O estado VAZIO já explica o que é um transbordo — mas só quando a fila
        está vazia, que é justamente quando ninguém precisa saber. Aqui a
        explicação fica disponível com a fila cheia, que é quando alguém novo
        vai perguntar. Os motivos aparecem abreviados em cada cartão e em
        nenhum lugar por extenso; "úteis" está na etiqueta de espera sem dizer
        o que significa.
      */}
      <BlocoDeAjuda
        secoes={[
          {
            id: 'como',
            titulo: 'Como esta fila funciona',
            corpo: (
              <>
                <Text size="sm">
                  <strong>Transbordo é o bot passando a conversa para gente.</strong> Ele acontece
                  por quatro motivos, que aparecem abreviados em cada cartão:{' '}
                  <strong>pediu atendente</strong> (o aluno pediu);{' '}
                  <strong>lacuna na base</strong> (o bot não achou resposta que servisse);{' '}
                  <strong>dado de pagamento</strong> (o assunto envolve valor ou comprovante, e o
                  bot é proibido de tratar isso sozinho); <strong>humano assumiu</strong> (alguém
                  da secretaria entrou na conversa pelo WhatsApp e o bot se calou).
                </Text>
                <Text size="sm">
                  <strong>A espera é contada em tempo de atendimento, não de relógio.</strong> É o
                  que a palavra <em>úteis</em> na etiqueta quer dizer: o que corre fora do horário
                  de atendimento e no fim de semana não conta. Um transbordo que chegou sábado às
                  10h e foi atendido segunda às 9h não esperou 47 horas — esperou os minutos de
                  segunda. Passadas <strong>4h úteis</strong>, o cartão fica vermelho.
                </Text>
                <Text size="sm">
                  <strong>“Marcar como atendido” é registro interno. O aluno não é avisado.</strong>{' '}
                  Ele tira o item da fila, guarda seu nome, a hora e a observação que você
                  escrever. Quem fala com o aluno é você, por WhatsApp ou telefone — o painel não
                  envia mensagem. Marcar sem ter falado com a pessoa deixa a fila limpa e a pessoa
                  esperando. Errou? <em>Devolver à fila</em> desfaz.
                </Text>
                <Text size="sm">
                  <strong>A fila não é o único aviso.</strong> No momento do transbordo o bot
                  também avisa o grupo da secretaria no WhatsApp, e é lá que aparece o{' '}
                  <strong>número completo</strong> do aluno — aqui ele sai mascarado, porque esta é
                  tela de balcão. Esta fila existe porque a mensagem do grupo some na rolagem e ela
                  não some.
                </Text>
                <Text size="sm" c="dimmed">
                  O selo <em>sombra</em> num cartão quer dizer que, naquele atendimento, o bot
                  estava em modo de teste e o aluno não recebeu resposta nenhuma — nem a de que
                  seria transferido. Se for para falar com a pessoa, comece do começo.
                </Text>
              </>
            ),
          },
        ]}
      />

      {atrasados > 0 && (
        <Alert color="orange" variant="light" title={`${atrasados} transbordo(s) esperando há mais de 4 horas de atendimento`}>
          A contagem é em tempo útil: o que entrou sexta à noite não conta o fim de semana.
        </Alert>
      )}

      {erro && (
        <Alert color="red" variant="light" role="alert">
          {erro}
        </Alert>
      )}

      <Text size="sm" c="dimmed">
        {dados.itens.length} na tela · {dados.pendentes} pendente(s) no total
      </Text>

      {dados.itens.length === 0 &&
        (status === 'pendente' ? (
          <Vazio titulo="Nenhum transbordo esperando.">
            Tudo que o bot passou adiante já foi atendido. Quando ele não conseguir responder ou o
            aluno pedir atendente, a conversa cai aqui com protocolo e tempo de espera — e o
            contador na lateral avisa sem você abrir esta tela.
          </Vazio>
        ) : (
          <Vazio titulo="Nada neste filtro.">
            Troque o status para <strong>Pendentes</strong> para ver quem ainda espera, ou amplie o
            período: o filtro padrão mostra os últimos 30 dias.
          </Vazio>
        ))}

      {dados.itens.map((item) => (
        <Card
          key={item.atendimentoId}
          withBorder
          padding="sm"
          /* Fundo de atenção para o atrasado — e a etiqueta de espera ao lado
             diz o mesmo em texto, porque cor sozinha não é sinal. */
          bg={item.atrasado ? 'var(--mantine-color-red-light)' : undefined}
        >
          <Stack gap="xs">
            <Group gap="sm" wrap="wrap">
              <Text fw={600}>{item.protocolo}</Text>
              <NomeDoAluno nome={item.nome} origem={item.nomeOrigem} />
              {/* Antes de pegar o transbordo: piloto formado pedindo
                  revalidação é atendimento diferente de primeira dúvida. */}
              <OrigemDoContato
                tipo={item.tipo}
                curso={item.curso}
                anac={item.anac}
                noSaga={item.noSaga}
              />
              <Text size="sm" c="dimmed" ff="monospace">
                {item.telefoneMascarado ?? '—'}
              </Text>
              <Text size="xs" c="dimmed">
                {new Date(item.transbordouEm).toLocaleString('pt-BR')}
              </Text>
              <Badge color={item.atrasado ? 'red' : 'gray'} variant={item.atrasado ? 'filled' : 'light'}>
                {esperaUtil(item.minutosUteis)}
              </Badge>
              {item.modo === 'shadow' && (
                <Badge color="orange" title="nada foi enviado ao aluno">
                  sombra
                </Badge>
              )}
            </Group>

            {/* Em ordem de utilidade: a pergunta digitada, ou a opção de menu
                escolhida. "Sem pergunta" sozinho era verdade e inútil. */}
            {item.pergunta ? (
              <Paper withBorder p="xs" radius="md" maw={520}>
                <Text size="sm">{item.pergunta}</Text>
              </Paper>
            ) : item.opcaoEscolhida ? (
              <Text size="sm">
                <strong>Pediu:</strong> {item.opcaoEscolhida}
              </Text>
            ) : (
              <Text size="sm" c="dimmed">
                Sem pergunta — o aluno pediu atendente direto.
              </Text>
            )}

            {/* Com os dois, a opção também aparece: diz de ONDE veio a pergunta. */}
            {item.pergunta && item.opcaoEscolhida && (
              <Text size="xs" c="dimmed">
                Em: {item.opcaoEscolhida}
              </Text>
            )}

            <Text size="xs" c="dimmed">
              {ROTULO_MOTIVO[item.motivo] ?? item.motivo}
              {item.detalhe ? ` · ${item.detalhe}` : ''}
              {item.melhorScore !== null ? ` · melhor score ${item.melhorScore.toFixed(2)}` : ''}
            </Text>

            {item.status === 'atendido' && (
              <Text size="xs" c="dimmed">
                ✓ atendido{' '}
                {item.atendidoEm ? new Date(item.atendidoEm).toLocaleString('pt-BR') : ''}
                {item.atendidoPorNome ? ` por ${item.atendidoPorNome}` : ''}
                {item.observacao ? ` — “${item.observacao}”` : ''}
              </Text>
            )}

            <Group gap="xs">
              {/* Quem pega um transbordo precisa saber o que já foi dito à
                  pessoa antes de responder. Esse é o fio. */}
              {aoVerConversa && (
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => aoVerConversa(item.atendimentoId)}
                >
                  Ver conversa
                </Button>
              )}
              {/* Mesmo vocabulário do link dentro da conversa: era "Abrir
                  atendimento" quando havia uma seção com esse nome, e duas
                  palavras para a mesma coisa confundem. */}
              <Button size="xs" onClick={() => aoAbrirAtendimento(item.atendimentoId)}>
                Detalhe técnico
              </Button>

              {capacidades.editar && item.status === 'pendente' && (
                <Button
                  size="xs"
                  variant="light"
                  onClick={() => {
                    setObservacao('');
                    setAnotando(item.atendimentoId);
                  }}
                >
                  Marcar como atendido
                </Button>
              )}

              {capacidades.editar && item.status === 'atendido' && (
                <Button size="xs" onClick={() => void devolver(item)}>
                  Devolver à fila
                </Button>
              )}
            </Group>
          </Stack>
        </Card>
      ))}

      <Modal
        opened={emFoco !== null}
        onClose={() => setAnotando(null)}
        title={`Marcar ${emFoco?.protocolo ?? ''} como atendido`}
        centered
      >
        <Stack gap="sm">
          <TextInput
            label="O que foi feito (opcional)"
            description="Fica registrado com seu nome e a hora."
            maxLength={500}
            placeholder="ex.: liguei para o aluno e passei os valores"
            value={observacao}
            data-autofocus
            onChange={(e) => setObservacao(e.currentTarget.value)}
          />
          <Group justify="flex-end">
            <Button onClick={() => setAnotando(null)}>Cancelar</Button>
            <Button
              variant="filled"
              onClick={() => {
                if (emFoco) void marcar(emFoco, observacao);
              }}
            >
              Confirmar
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}
